import React, { memo, useRef, useLayoutEffect, useEffect, useContext, useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';

// utils
import { TextContext } from '@/utils/contexts/TextContext';
import getCaretOffset from '@/utils/functions/getCaretOffset';
import useWindowSize from '@/utils/hooks/useWindowSize';

// components
import LetterPopover from '@/views/pages/Letter/elements/LetterPopover/LetterPopover';
import TextChangeHandler from '@/views/pages/Letter/elements/TextChangeHandler';

import Spinner from '@/views/components/Spinner/Spinner';

///// ref={textNodeRef} - it has listener for mouseDown to start highlighting text, show popover and so on...
const LetterContent = ({ mode = 'originalDoc' }) => {
	// context
	const {
		contextDispatch, contextState,
	} = useContext(TextContext);
	const { user } = useSelector(state => state.auth);
	// hooks
	const windowSize = useWindowSize();

	// refs
	const textNodeRef = useRef(null);

	// local state
	const [ content, setContent ] = useState(null);
	const [ footnotes, setFootnotes ] = useState(null);
	const [ textNodeHasListener, setTextNodeHasListener ] = useState(false);

	// carret and popover vars
	const [ currentCaret, setCurrentCaret ] = useState({ start: 0, end: 0, text: '' });

	const [ letterWindowWidth, setLetterWindowWidth ] = useState(0);

	const popoverWidth = 500;

	// const [popoverMode, setPopoverMode] = useState(0);
	// 0 - select text
	// 1 - edit selection - disable select
	// 2 - change clicked - disable select

	// const [popoverDisablePointerEvents, setPopoverDisablePointerEvents] = useState(false);

	// change handlers vars
	const [ changeHandlers, setChangeHandlers ] = useState(null);
	const [ selectedChangeHandlerId, setSelectedChangeHandlerId ] = useState();

	useEffect(() => {
        
		if (mode && contextState.letterData && contextState.letterData[mode]) {
			setContent(contextState.letterData[mode].content);
			setFootnotes(contextState.letterData[mode].footnotes);
		}
		// eslint-disable-next-line
    }, [mode, contextState.letterData]);

	useEffect(() => {
		// when popover mode has changed remove selection change on modes 1 & 2
		if (contextState.popoverMode === 1 || contextState.popoverMode === 2) {
			document.removeEventListener('selectionchange', selectionChange);
			contextDispatch({ type: 'setCaret', payload: { start: 0, end: 0, text: '' } });
		} else if (contextState.popoverMode === 0) {
			document.addEventListener('selectionchange', selectionChange);
			if (selectedChangeHandlerId) {
				// when popover is hiding (popoverMode has changed to 0) - reset selectedChangeHandlerId
				setSelectedChangeHandlerId(null);
			}
		}
	}, [ contextState.popoverMode ]);

	useEffect(() => {
		if (textNodeRef.current) {
			setLetterWindowWidth(textNodeRef.current.getBoundingClientRect().width);
		}
	}, [ contextState.caret, contextState.popoverMode ]);

	useEffect(() => {
		if (changeHandlers) {
			changeHandlers.forEach((handler) => {
				const originalElement = document.querySelectorAll(`[data-change-id="${handler.changeId}"][data-type="original"]`)[0];
				if(originalElement) {
					originalElement.onmouseenter = () => {
						originalElement.classList.add("highlighted-change");
						if (handler.auto) {
							originalElement.classList.add("highlighted-change--auto");
						} else {
							originalElement.classList.add("highlighted-change--manual");
						}
						const simplifiedElement = document.querySelectorAll(`[data-change-id="${handler.changeId}"][data-type="simplified"]`)[0];
						if (simplifiedElement) {
							simplifiedElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
							if(simplifiedElement.innerHTML.trim() === '') {
								simplifiedElement.classList.add("highlighted-change-empty");
								if (handler.auto) {
									simplifiedElement.classList.add("highlighted-change-empty--auto");
								} else {
									simplifiedElement.classList.add("highlighted-change-empty--manual");
								}
							} else {
								simplifiedElement.classList.add("highlighted-change");
								if (handler.auto) {
									simplifiedElement.classList.add("highlighted-change--auto");
								} else {
									simplifiedElement.classList.add("highlighted-change--manual");
								}
							}
						}
					};
					originalElement.onmouseleave = () => {
						originalElement.classList.remove("highlighted-change");
						originalElement.classList.remove("highlighted-change--auto");
						originalElement.classList.remove("highlighted-change--manual");
					
						const simplifiedElement = document.querySelectorAll(`[data-change-id="${handler.changeId}"][data-type="simplified"]`)[0];
						if (simplifiedElement) {
							simplifiedElement.classList.remove("highlighted-change");
							simplifiedElement.classList.remove("highlighted-change--auto");
							simplifiedElement.classList.remove("highlighted-change--manual");
							simplifiedElement.classList.remove("highlighted-change-empty");
							simplifiedElement.classList.remove("highlighted-change-empty--auto");
							simplifiedElement.classList.remove("highlighted-change-empty--manual");
						}
					};
				}
			});
		}
	}, [ changeHandlers ]);

	useEffect(() => {
		remapHandlers(true);
	}, [ mode ]);

	// util to find proper popover position
	const locateX = (caretX, caretWidth, textWidth) => {
		if (textWidth < caretWidth + 2) {
			// selection width is equal to text width - stick popover to right bottom
			return Math.round(textWidth - popoverWidth + 30);
		} else {
			// selection is narrower than full text width - popover is floating
			return Math.round(Math.min(Math.max(caretX + (caretWidth / 2) - (popoverWidth / 2), 30), (textWidth - popoverWidth + 30)));
		}
	};

	useLayoutEffect(() => {
		refreshCaret();
		remapHandlers(true);

		// in mode 1 -> list of marks -> last of them determines the popover position
		if (!textNodeRef.current) return;

		if (contextState.popoverMode === 1) {
			const list = document.getElementsByTagName("MARK");
			const elementRect = (list[list.length - 1].getBoundingClientRect());

			contextDispatch({
				type: 'setPopoverPosition', popoverPosition: {
					x: locateX(
						elementRect.left,
						elementRect.width,
						textNodeRef.current.getBoundingClientRect().width),
					y: Math.round(elementRect.bottom - textNodeRef.current.getBoundingClientRect().y + 50),
				},
			});
		}
		setLetterWindowWidth(textNodeRef.current.getBoundingClientRect().width);
		// eslint-disable-next-line
    }, [windowSize]);

	const textMouseDown = () => {
		// first - mouse is down on text
		if (user.role !== "reader" && !contextState.isMobile && contextState.popoverMode === 0) {
			contextDispatch({
				type: 'setPopoverDisablePointerEvents',
				payload: { popoverDisablePointerEvents: true },
			});
			document.removeEventListener('mouseup', documentMouseUp);
			document.addEventListener('mouseup', documentMouseUp, { once: true });
		}
	};

	const checkAncestorHasClass = (element, classname) => {
		if (element && element.className) {
			if (element.className.split(' ').indexOf(classname) >= 0) {
				return true;
			}
		}
		return element && element.parentNode && checkAncestorHasClass(element.parentNode, classname);
	};

	const selectionChange = useCallback(() => {
		// user is selecting next letters - caret is changing, so the position of popover is changing by useEffect

		// if one of ancestors of selection has css class letter__editor
		if (checkAncestorHasClass(window.getSelection().anchorNode, "letter__editor")) {
			refreshCaret();
		} else {
			contextDispatch({ type: 'setCaret', payload: { start: 0, end: 0, text: '' } });
			// setCaret({ start: 0, end: 0, text: '' });
		}
		// otherwise selection is not in letter editor - do
		// eslint-disable-next-line
    }, [contextDispatch]);

	useEffect(() => {
		if (contextState.caret && contextState.caret.boundingRect && mode === "originalDoc") {
			if (textNodeRef.current) {
				contextDispatch({
					type: 'setPopoverPosition', popoverPosition: {
						x: locateX(
							contextState.caret.boundingRect.left,
							contextState.caret.boundingRect.width,
							textNodeRef.current.getBoundingClientRect().width),
						y: Math.round(contextState.caret.boundingRect.bottom - textNodeRef.current.getBoundingClientRect().y + 50),
					},
				});
			}
		}
		// eslint-disable-next-line
    }, [contextState.popoverDisablePointerEvents, contextState.caret]);

	useEffect(() => {
		if (currentCaret.text.length > 0) {
			contextDispatch({ type: 'setCaret', payload: currentCaret });
		} else {
			contextDispatch({ type: 'setCaret', payload: { start: 0, end: 0, text: '' } });
		}
		// eslint-disable-next-line
    }, [currentCaret]);

	const refreshCaret = () => {
		if (textNodeRef.current) {
			const currentCaretOffset = getCaretOffset(textNodeRef.current);
			setCurrentCaret(currentCaretOffset);
		}
	};
	const documentMouseUp = () => {
		// finally user releases mouse
		contextDispatch({
			type: 'setPopoverDisablePointerEvents',
			payload: { popoverDisablePointerEvents: false },
		});
		document.removeEventListener('mouseup', documentMouseUp);
	};
	// end of caret handling

	// HANDLERS
	useLayoutEffect(() => {
		if (mode !== "originalDoc") return;
		remapHandlers();
		// eslint-disable-next-line
    });

	const remapHandlers = (force = false) => {
		if (mode !== "originalDoc") return;
		// wait until whole text is rendered
		if (changeHandlers && !force) {
			return;
		}
		const list = Array.from(document.getElementsByClassName("change"));
		if (list.length) {
			const mapped = list.map((element) => {
				// when resizing window - check there is any change selected (popover is visible), if so - relocate popover
				if (element.dataset.changeId === selectedChangeHandlerId && element.dataset?.quantity) {
					contextDispatch({
						type: 'setPopoverPosition', popoverPosition: {
							x: locateX(
								element.offsetLeft, 1,
								textNodeRef.current.getBoundingClientRect().width,
							),
							y: Math.round(element.offsetTop + 30),
						},
					});
				}
				return (
					{
						startX: element.offsetLeft,
						startY: element.offsetTop,
						id: element.id,
						quantity: element.dataset.quantity,
						changeId: element.dataset.changeId,
						auto: element.dataset.auto,
					}
				);
			});
			setChangeHandlers(mapped);
		} else {
			setChangeHandlers(null);
		}
	};
	const onChangeHandlerClicked = useCallback((changeId, changeHandlerX, changeHandlerY) => {
		if (mode !== "originalDoc") return;
		contextDispatch({
			type: 'setPopoverMode', payload: {
				popoverMode: 2,
			},
		});
		// loadChangesHistory(changeId);
		contextDispatch({ type: 'loadChangesHistory', payload: { changeId: changeId } });

		setSelectedChangeHandlerId(changeId);

		contextDispatch({
			type: 'setPopoverPosition', popoverPosition: {
				x: locateX(
					changeHandlerX,
					1,
					textNodeRef.current.getBoundingClientRect().width,
				),
				y: Math.round(changeHandlerY + 30),
			},
		});
	}, [ mode ]);

	useEffect(() => {
		if (mode !== "originalDoc") return;
		setTimeout(() => {
			remapHandlers(true);
		}, 150);
		// eslint-disable-next-line
    }, [contextState.letterData]);

	// handle DOMSubtreeModified listener
	const textDomSubtreeModified = useCallback(() => {
		// text tree is modified, remap
		if (mode !== "originalDoc") return;
		remapHandlers(true);
		// eslint-disable-next-line
    }, []);

	useLayoutEffect(() => {
		if (!textNodeHasListener && textNodeRef.current) {
			setTextNodeHasListener(true);
			textNodeRef.current.addEventListener('DOMSubtreeModified', textDomSubtreeModified);
		}
		// eslint-disable-next-line
    }, []);
	useEffect(() => {
		// unmount
		return (() => {
			if (textNodeRef.current) {
				textNodeRef.current.removeEventListener('DOMSubtreeModified', textDomSubtreeModified);
			}
		});
		// eslint-disable-next-line
    }, []);

	// contextState.letterData
	if (!content) {
		return <Spinner addClass='table__spinner' />;
	}

	return (
		<>
			{!!content.length && (
				<article
					className='letter__text--text'
					style={ { position: 'relative' } }
				>
					{mode === "originalDoc" && (
						<LetterPopover
							popoverWidth={ popoverWidth }
							letterWindowWidth={ letterWindowWidth }
						/>
					)}
					{ contextState.autoChangesIsLoading && (
						<div className={ `letter__text--auto-loading-spinner${mode==='originalDoc' ? ' left' : ''}` }>
							<Spinner />
						</div>
					)}

					{mode === 'originalDoc' ? (
						<div
							ref={ textNodeRef }
							className={ `letter__editor ${(contextState.popoverMode !== 0) ? 'noselect' : ''}` }
							dangerouslySetInnerHTML={ { __html: content } }
							onMouseDown={ textMouseDown }
						/>
					) : (
						<div
							dangerouslySetInnerHTML={ { __html: content } }
						/>
					)}
					{mode === "originalDoc" && changeHandlers && changeHandlers.length && changeHandlers.map((element, index) =>
						(
							<TextChangeHandler
								key={ 'handler' + index }
								data={ element }
								onChangeHandlerClicked={ onChangeHandlerClicked }
								disabled={ contextState.popoverMode === 1 }
								active={ element.changeId === selectedChangeHandlerId }
							/>
						),
					)}

					{footnotes.length > 0 && (
						<div className='letter__text--footnotes'>
							{footnotes.map((el, index) => (
								<div
									key={ index }
									dangerouslySetInnerHTML={ { __html: el } }
								/>
							))}
						</div>
					)}
				</article>
			)}
		</>
	);
};

LetterContent.propTypes = {
	mode: PropTypes.string,
};

export default memo(LetterContent);